import { action, computed, makeObservable, observable } from 'mobx';
import { ResidentialAccountType } from 'core/constants/residential-account-types';
import { FeatureFlag } from 'core/enums/feature-flag';
import { PaymentMethodType } from 'core/enums/payment-method';
import { PermissionType } from 'core/enums/permission-type';
import { AccountDetailsDTO } from 'core/models/account/account-details.dto';
import { AccountsDetailsDTO } from 'core/models/account/amount-to-pay.dto';
import { AccountMahService } from 'core/services/api/account-mah';
import { FeatureStore } from 'core/stores/feature.store';
import { SiteConfigStore } from 'core/stores/site-config.store';

export class AccountStore {
  accountNumber!: number;
  accountType = '';
  statusActive!: string;
  billingDate!: number;
  paymentMethod!: PaymentMethodType;
  isLoadingPay = true;
  isLoadingDetails = true;
  isMAH = false;

  get isLoading() {
    return this.isLoadingPay || this.isLoadingDetails;
  }

  constructor() {
    makeObservable(this, {
      isLoadingPay: observable,
      isLoadingDetails: observable,
      accountNumber: observable,
      accountType: observable,
      statusActive: observable,
      billingDate: observable,
      paymentMethod: observable,
      isMAH: observable,
      changeAccountNumber: action,
      changeAccountType: action,
      changeStatusActive: action,
      changeBillingDate: action,
      changePaymentMethod: action,
      changeIsLoadingPay: action,
      changeIsLoadingDetails: action,
      isLoading: computed
    });
  }

  get readableMOP() {
    if (this.paymentMethod === PaymentMethodType.DebitOrderCurrent || this.paymentMethod === PaymentMethodType.DebitOrderSavings) {
      return PaymentMethodType.DebitOrder;
    }
    return this.paymentMethod;
  }

  initialize(customerNumber: string, country: string, accountNumber: number, featureMap: Map<string, PermissionType>) {
    this.changeIsLoadingDetails(true);

    AccountMahService.getAmountToPay(customerNumber, country).then(
      (data: AccountsDetailsDTO) => {
        const account = SiteConfigStore.getAccount(data?.accounts);

        if (!account) {
          console.error('Failed to retrieve account details')
          return;
        }

        this.changeAccountNumber(account.accountNumber);
        this.changeAccountType('Residential');
        this.changePaymentMethod(account.methodOfPayment);
        this.changeIsLoadingDetails(false);
      },
      (err) => {
        console.error('Failed to get MAH account details -> ', err);
        this.changeIsLoadingDetails(false);
      }
    );

    if (FeatureStore.hasViewPermission(featureMap, FeatureFlag.ViewBalanceAccountDetails)) {
      this.changeIsLoadingPay(true);

      AccountMahService.getAccountDetails(customerNumber, country, accountNumber).then(
        (data: AccountDetailsDTO) => {
          const account = SiteConfigStore.getAccount(data?.financialAccounts);

          if (!account) {
            console.error('Failed to retrieve account details')
            return;
          }

          this.changeAccountNumber(account?.number);
          this.changeAccountType('Residential');
          this.changeStatusActive(account?.status === 'Open' ? 'Active' : 'De-activated');
          this.changeBillingDate(account?.paymentDetails?.bankingDetails?.debitOrderDay);
          this.changePaymentMethod(account?.paymentDetails?.paymentType);
          this.changeIsLoadingPay(false);
        },
        (err) => {
          console.error('Failed to get account details -> ', err);
          this.changeIsLoadingPay(false);
        }
      );
    }
  }

  changeAccountNumber(accountNumber: number) {
    this.accountNumber = accountNumber;
  }

  changeAccountType(accountType: string) {
    this.accountType = accountType;
  }

  changeStatusActive(statusActive: string) {
    this.statusActive = statusActive;
  }

  changeBillingDate(billingDate: number) {
    this.billingDate = billingDate;
  }

  changePaymentMethod(paymentMethod: PaymentMethodType) {
    this.paymentMethod = paymentMethod;
  }

  changeIsLoadingPay(isLoadingPay: boolean) {
    this.isLoadingPay = isLoadingPay;
  }

  changeIsLoadingDetails(isLoadingDetails: boolean) {
    this.isLoadingDetails = isLoadingDetails;
  }
}